import { baseUrl, requestHeadersBasicAuth, requestHeadersJsonBasicAuth } from './general-api';
import { HamiltonProductList, HamiltonWishList, wishList } from '../building-blocks/wish-list/wish-list.module';
import { v4 as generateUUID } from 'uuid';
import { userData } from '../user-data.module';
import {
  isUserLoggedIn,
  getAnonymousWishlists,
  createAnonymousWishlist,
  renameAnonymousWishlist,
  deleteAnonymousWishlist,
  addItemToAnonymousWishlist,
  deleteItemFromAnonymousWishlist
} from './wish-list-local-storage';

/**
 * Base URL for the wish list API.
 */
export const baseWishlistPath = baseUrl + '/.rest/nodes/v1/userdata/favorites';

/**
 * Base URL to retrieve all wish lists.
 */
export const baseWishlistGetAllPath = baseUrl + '/.rest/delivery/v1/catalog/userdata';

/**
 * Base URL to retrieve all wish lists.
 */
export const baseEasyReferenceCardsPath = baseUrl + '/.rest/delivery/v1/catalog/reference-cards';

/**
 * Returns all wish lists
 */
export const getWishListsAndEasyReferenceCards = async (): Promise<HamiltonWishList> => {
  return new Promise((resolve, reject) => {
    getAllWishlists()
      .then((wishLists) => {
        getAllEasyReferenceCards()
          .then((referenceCards) => {
            const wishListData = {
              wishLists,
              easyReferenceLists: referenceCards
            };
            resolve(wishListData);
          })
          .catch(() => {
            reject();
          });
      })
      .catch(() => {
        reject();
      });
  });
};

/**
 * Returns all wish lists
 */
export const getAllWishlists = (): Promise<HamiltonProductList[]> => {
  if (!isUserLoggedIn()) {
    return Promise.resolve(getAnonymousWishlists());
  }
  return new Promise((resolve, reject) => {
    let url = baseWishlistGetAllPath;
    const params: Record<string, string> = {};

    if (userData.lastSelectedCustomerId) {
      params.customerNo = userData.lastSelectedCustomerId;
    }

    if (Object.keys(params).length > 0) {
      const parameterString = new URLSearchParams(params).toString();
      url = `${url}?${parameterString}`;
    }

    return fetch(url, {
      headers: requestHeadersBasicAuth
    })
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          const favorites = data.favorites;
          const availableNodes = favorites['@nodes'];
          const wishLists = [];

          for (const node of availableNodes) {
            wishLists.push(favorites[node]);
          }
          resolve(wishLists);
        }

        resolve([]);
      })
      .catch(() => {
        reject();
      });
  });
};

/**
 * Returns all wish lists
 */
export const getAllEasyReferenceCards = (): Promise<HamiltonProductList[]> => {
  let url = baseEasyReferenceCardsPath;

  const params: Record<string, string> = {};

  if (userData.lastSelectedCustomerId) {
    params.customerNo = userData.lastSelectedCustomerId;
  }

  if (Object.keys(params).length > 0) {
    const parameterString = new URLSearchParams(params).toString();
    url = `${url}?${parameterString}`;
  }

  return fetch(url, {
    headers: requestHeadersBasicAuth
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.error) {
        return [];
      }
      return data || [];
    })
    .catch(() => {
      return [];
    });
};

/**
 * Adds the product with the given id to a given wish list.
 * @param productId: Id of the product that should be added to the wish list.
 * @param wishListId: Id of the wish list that this product should be added to.
 * @param currentProducts: All products (their IDs) that are currently in the wish list.
 */
export const addItemToWishList = (
  productId: string,
  currentProducts: string[],
  wishListId: string
): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return addItemToAnonymousWishlist(productId, wishListId).then((success) => {
      if (success) {
        wishList.reloadWishlist(document.querySelector<HTMLElement>('.wish-list'));
      }
      return success;
    });
  }

  return new Promise((resolve, reject) => {
    const wishListWithNewItem = [...currentProducts, productId];

    const updatedWishList = {
      properties: [
        {
          name: 'items',
          type: 'String',
          multiple: true,
          values: wishListWithNewItem
        }
      ]
    };

    fetch(`${baseWishlistPath}/${wishListId}`, {
      method: 'POST',
      headers: requestHeadersJsonBasicAuth,
      body: JSON.stringify(updatedWishList)
    })
      .then(() => {
        wishList.reloadWishlist(document.querySelector<HTMLElement>('.wish-list'));
        resolve(true);
      })
      .catch(() => {
        reject();
      });
  });
};

/**
 * Removes the product with the given id from wish list.
 * @param productId: Id of the product that should be removed from the wish list.
 * @param wishListId: Id of the wish list that this product is currently part of.
 * @param currentProducts: All products (their IDs) that are currently in the wish list.
 */
export const deleteItemFromWishList = (
  productId: string,
  currentProducts: string[],
  wishListId: string
): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return deleteItemFromAnonymousWishlist(productId, wishListId);
  }

  const wishListWithoutGivenProduct = currentProducts.filter((item) => item !== productId);

  const updatedWishList = {
    properties: [
      {
        name: 'items',
        type: 'String',
        multiple: true,
        values: wishListWithoutGivenProduct
      }
    ]
  };

  return fetch(`${baseWishlistPath}/${wishListId}`, {
    method: 'POST',
    headers: requestHeadersJsonBasicAuth,
    body: JSON.stringify(updatedWishList)
  })
    .then((response) => {
      //wishList.reloadWishlist(document.querySelector<HTMLElement>('.wish-list'));
      return response.ok;
    })
    .catch(() => {
      return false;
    });
};

/**
 * Create a new wish list with the given name.
 */
export const createNewWishlist = (wishlistName: string): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return createAnonymousWishlist(wishlistName);
  }

  const listId = generateUUID();

  const newWishlist = {
    name: listId,
    type: 'mgnl:contentNode',
    properties: [
      {
        name: 'name',
        type: 'String',
        multiple: false,
        values: [wishlistName] // The name of the wishlist
      }
    ]
  };

  return fetch(baseWishlistPath, {
    method: 'PUT',
    headers: requestHeadersJsonBasicAuth,
    body: JSON.stringify(newWishlist)
  })
    .then((response) => {
      return response.ok;
    })
    .catch(() => {
      return false;
    });
};

/**
 * Delete the wish list with the given ID.
 */
export const deleteWishlist = (wishlistId: string): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return deleteAnonymousWishlist(wishlistId);
  }

  const wishlistToDelete = {
    properties: [
      {
        name: 'name',
        type: 'String',
        multiple: false,
        values: [] // The name of the wishlist
      }
    ]
  };

  return fetch(`${baseWishlistPath}/${wishlistId}`, {
    method: 'DELETE',
    headers: requestHeadersJsonBasicAuth,
    body: JSON.stringify(wishlistToDelete)
  })
    .then((response) => {
      return response.ok;
    })
    .catch(() => {
      return false;
    });
};

/**
 * Rename an existing wish list.
 * @param wishlistId: The id of the wish list that should be renamed.
 * @param newWishlistName: The new name for the wish list.
 */
export const renameWishlist = (wishlistId: string, newWishlistName: string): Promise<boolean> => {
  if (!isUserLoggedIn()) {
    return renameAnonymousWishlist(wishlistId, newWishlistName);
  }

  const newWishlist = {
    properties: [
      {
        name: 'name',
        type: 'String',
        multiple: false,
        values: [newWishlistName] // The new name of the wishlist
      }
    ]
  };

  return fetch(`${baseWishlistPath}/${wishlistId}`, {
    method: 'POST',
    headers: requestHeadersJsonBasicAuth,
    body: JSON.stringify(newWishlist)
  })
    .then((response) => {
      return response.ok;
    })
    .catch(() => {
      return false;
    });
};

/**
 * Get the URL for the pdf for the given wish list.
 * @param wishlistId: The id of the wish list for which the pdf link should be returned.
 */
export const getUrlForPdf = (wishlistId: string): string => {
  if (!isUserLoggedIn()) {
    const wishlists = getAnonymousWishlists();
    const wishlist = wishlists.find(
      (list) => list['@name'] === wishlistId || list['@id'] === wishlistId
    );
    if (wishlist) {
      const itemIds = (wishlist.items || [])
        .map((item) => item['@id'] || (item as any).nodeid)
        .filter(Boolean);
      const params = new URLSearchParams();
      if (wishlist.name) {
        params.set('name', wishlist.name);
      }
      if (itemIds.length > 0) {
        params.set('items', itemIds.join(','));
      }
      return `${baseUrl}/.pdfhtml-wishlist/anonymous?${params.toString()}`;
    }
  }

  return `${baseUrl}/.pdfhtml-wishlist/${encodeURIComponent(wishlistId)}`;
};
